import type { Hex } from 'viem';
import { encodeAbiParameters, encodeFunctionData, keccak256 } from 'viem';

import {
  BANK_DEPOSIT_ADDRESS,
  BASE_CROSS_DOMAIN_DEPOSIT_ADDRESS,
  BASE_L1_MESSENGER_ADDRESS,
  ERC721_ADDRESS,
  ETHEREUM_CHAIN_ID,
  MIGRATOR_ADDRESS,
  RELAY_ADDRESS,
  STARKEX_ADDRESS,
} from '#src/config.ts';

export const NULL_ADDRESS = '0x0000000000000000000000000000000000000000';

type ValueTypeParameter =
  | string
  | number
  | boolean
  | (string | number | boolean)[];

const encode = (types: string[], values: ValueTypeParameter[]) =>
  encodeAbiParameters(
    types.map(type => ({ type })),
    values
  );

const approveMessage = (
  nonce: string | number,
  address: Hex,
  batchCall: boolean
) => {
  const extraGas = 47563;
  const encodedFunction = encodeFunctionData({
    abi: [
      {
        type: 'function',
        name: 'setApprovalForAll',
        inputs: [
          { name: 'operator', type: 'address' },
          { name: 'approved', type: 'bool' },
        ],
        // Unused but necessary for viem
        outputs: [],
        stateMutability: 'pure',
      },
    ],
    args: [address, true],
  });

  return {
    encodedFunction,
    extraGas,
    message: encode(
      ['address', 'bytes', 'uint256', 'bool', 'address'],
      [ERC721_ADDRESS, encodedFunction, nonce, batchCall, RELAY_ADDRESS]
    ),
  };
};

export const approveMigratorMessage = (nonce: string | number) => {
  return approveMessage(nonce, MIGRATOR_ADDRESS, true);
};

export const migrateCardsMessage = (
  cardIds: string[],
  expirationBlock: string | number
) => encode(['uint256[]', 'uint32'], [cardIds, expirationBlock]);

export const depositETHtoBaseBankTx = (sorareAddress: string) => {
  const data = encodeFunctionData({
    abi: [
      {
        inputs: [
          {
            internalType: 'address',
            name: '_sorareAddress',
            type: 'address',
          },
        ],
        name: 'deposit',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
      },
    ],
    args: [sorareAddress as Hex],
  });

  return {
    to: BANK_DEPOSIT_ADDRESS as Hex,
    data,
  };
};

export const computeFactData = (
  destinationAddress: string,
  amount: bigint,
  salt: string
) => {
  const fact = keccak256(
    encode(
      ['address', 'uint256', 'bytes32'],
      [destinationAddress, amount.toString(), salt]
    )
  );

  return encode(['bytes32'], [fact]);
};

export const ethereumBankTransferMessage = (transfer: {
  senderAddress: string;
  receiverAddress: string;
  amount: bigint;
  feeAmount: bigint;
  deadline: string;
  salt: string;
  proxyAddress: string;
  data: string;
  contractAddress: string;
}) => {
  return encode(
    [
      'address',
      'address',
      'uint256',
      'uint256',
      'uint64',
      'bytes32',
      'address',
      'bytes',
      'address',
    ],
    [
      transfer.senderAddress,
      transfer.receiverAddress,
      transfer.amount.toString(),
      transfer.feeAmount.toString(),
      transfer.deadline,
      transfer.salt,
      transfer.proxyAddress,
      transfer.data,
      transfer.contractAddress,
    ]
  );
};

export const escapeWithdrawWithFeeMessage = (
  fee: bigint,
  feeRecipient: string
) =>
  encode(
    ['string', 'uint256', 'address', 'uint256', 'address'],
    [
      'withdrawWithFee',
      ETHEREUM_CHAIN_ID,
      STARKEX_ADDRESS,
      fee.toString(),
      feeRecipient,
    ]
  );

export const escapeWithdrawWithFeeToBaseBankMessage = (
  fee: bigint,
  feeRecipient: string,
  sorareAddress: string
) =>
  encode(
    [
      'string',
      'uint256',
      'address',
      'uint256',
      'address',
      'address',
      'address',
      'address',
      'uint32',
    ],
    [
      'withdrawWithFeeToBaseBank',
      ETHEREUM_CHAIN_ID,
      STARKEX_ADDRESS,
      fee.toString(),
      feeRecipient,
      BASE_L1_MESSENGER_ADDRESS,
      BASE_CROSS_DOMAIN_DEPOSIT_ADDRESS,
      sorareAddress,
      0,
    ]
  );

export const computeBridgeFact = (condition: {
  conditionalAmount: bigint;
  conditionalSalt: string;
  conditionalReceiverAddress: string;
  conditionalBridgeCallerAddress: string;
  conditionalNetwork: string;
  conditionalCurrency: string;
}) => {
  const fact = keccak256(
    encode(
      ['bytes32', 'uint246', 'address'],
      [
        condition.conditionalSalt,
        condition.conditionalAmount.toString(),
        condition.conditionalReceiverAddress,
      ]
    )
  );

  const wrappedFact = keccak256(
    encode(
      ['bytes32', 'address'],
      [fact, condition.conditionalBridgeCallerAddress]
    )
  );

  return wrappedFact;
};
